import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminAnalytics, adminLogout, getAdminToken } from '../../api/client';

function Sparkline({ data, height = 42 }) {
  if (!data?.length) return <div className="muted">No data</div>;
  const max = Math.max(...data.map((d) => d.clicks), 1);
  const w = 100 / data.length;
  return (
    <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="sparkline">
      {data.map((d, i) => {
        const h = (d.clicks / max) * (height - 4);
        return (
          <rect
            key={d.date ?? `spark-${i}`}
            x={i * w + w * 0.15}
            y={height - h - 2}
            width={w * 0.7}
            height={h}
            className="spark-bar"
          >
            <title>{`${d.date}: ${d.clicks} clicks`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!getAdminToken()) {
      nav('/admin/login');
      return;
    }
    setData(null);
    adminAnalytics(days)
      .then(setData)
      .catch(async () => {
        await adminLogout();
        nav('/admin/login');
      });
  }, [days, nav]);

  const logout = async () => {
    await adminLogout();
    nav('/admin/login');
  };

  if (err) return <div className="page-container"><div className="empty-state">{err}</div></div>;
  if (!data) return <div className="page-container"><div className="empty-state">Loading…</div></div>;

  const t = data.totals;

  return (
    <div className="page-container admin-page">
      <div className="admin-topbar">
        <h1 className="page-title">Analytics</h1>
        <div className="admin-topbar-actions">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <Link to="/admin/tools" className="pill-btn">Manage Tools</Link>
          <Link to="/admin/ai" className="pill-btn" data-testid="admin-nav-ai">AI &amp; Newsletter</Link>
          <button className="pill-btn ghost" onClick={logout} type="button">Log out</button>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><span className="muted">Total tools</span><b>{t.total_tools.toLocaleString()}</b></div>
        <div className="stat-card"><span className="muted">Active</span><b>{t.active_tools.toLocaleString()}</b></div>
        <div className="stat-card"><span className="muted">Total clicks (all time)</span><b>{t.total_clicks.toLocaleString()}</b></div>
        <div className="stat-card"><span className="muted">Clicks (last {t.days}d)</span><b>{t.recent_clicks.toLocaleString()}</b></div>
      </div>

      <section className="analytics-section">
        <h2>Daily clicks</h2>
        <div className="chart-wrap">
          <Sparkline data={data.daily_clicks} height={100} />
        </div>
      </section>

      <div className="analytics-grid">
        <section className="analytics-section">
          <h2>Top tools</h2>
          {data.top_tools.length === 0 ? (
            <div className="muted">No clicks in this period yet.</div>
          ) : (
            <ol className="rank-list">
              {data.top_tools.map((r) => (
                <li key={r.slug}>
                  <Link to={`/tool/${r.slug}`}>{r.name}</Link>
                  <span className="rank-count">{r.clicks}</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="analytics-section">
          <h2>Top categories</h2>
          {data.top_categories.length === 0 ? (
            <div className="muted">No clicks yet.</div>
          ) : (
            <ol className="rank-list">
              {data.top_categories.map((r) => (
                <li key={r.category}>
                  <span>{r.category}</span>
                  <span className="rank-count">{r.clicks}</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="analytics-section">
          <h2>Devices</h2>
          <ul className="rank-list">
            {data.devices.map((d) => (
              <li key={d.device}>
                <span>{d.device}</span>
                <span className="rank-count">{d.clicks}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="analytics-section">
          <h2>Destination type</h2>
          <ul className="rank-list">
            {data.destinations.map((d) => (
              <li key={d.type}>
                <span>{d.type}</span>
                <span className="rank-count">{d.clicks}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
